import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { CircularProgress } from '@mui/material'

import { useAppDispatch } from '../../redux/store'
import {
  loginEmail,
  loginGoogle,
} from '../../redux/controlPlane/controlPlaneActions'
import {
  selectControlPlaneToken,
  selectControlPlaneAuthLoading,
  selectControlPlaneAuthError,
} from '../../redux/controlPlane/controlPlaneSelectors'
import GoogleSignInButton from '../../components/common/GoogleSignInButton'
import { AuthPasswordInput, AuthShell } from '../auth/AuthShell'

const AccountLogin: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const token = useSelector(selectControlPlaneToken)
  const loading = useSelector(selectControlPlaneAuthLoading)
  const error = useSelector(selectControlPlaneAuthError)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  useEffect(() => {
    if (token) navigate('/account/organizations')
  }, [token, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await dispatch(loginEmail({ email, password }))
  }

  return (
    <AuthShell
      foot={
        <>
          {t('account.noOrgYet', "Don't have an organization yet?")}{' '}
          <button
            type="button"
            className="incyia-auth-link"
            onClick={() => navigate('/get-started')}
          >
            {t('account.getStartedLink', 'Get started')}
          </button>
        </>
      }
    >
      <h1 className="incyia-auth-title">
        {t('account.loginTitle', 'Manage your organization')}
      </h1>
      <p className="incyia-auth-sub">
        {t('account.loginSubtitle', 'Sign in to manage billing and plans.')}
      </p>

      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <GoogleSignInButton
          onToken={(idToken) => dispatch(loginGoogle(idToken))}
        />
      </div>

      <div className="incyia-auth-or">
        <span>{t('auth.orPassword', 'or sign in with a password')}</span>
      </div>

      {error ? <div className="incyia-auth-error">{error}</div> : null}

      <form onSubmit={handleSubmit}>
        <div className="incyia-auth-field">
          <label className="incyia-auth-label" htmlFor="account-email">
            {t('auth.email')}
            <span className="incyia-auth-req">*</span>
          </label>
          <input
            id="account-email"
            className="incyia-auth-inp"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </div>

        <div className="incyia-auth-field">
          <label className="incyia-auth-label" htmlFor="account-password">
            {t('auth.password')}
            <span className="incyia-auth-req">*</span>
          </label>
          <AuthPasswordInput
            id="account-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        <button
          type="submit"
          className="incyia-auth-submit"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: '#fff' }} />
          ) : (
            t('auth.signIn')
          )}
        </button>
      </form>
    </AuthShell>
  )
}

export default AccountLogin
